import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export const useLogoUrl = (hasLogo, version, path = "/settings/logo") => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!hasLogo) { setUrl(null); return; }
    let obj, cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const r = await api.get(path, { responseType: "blob" });
        if (cancelled) return;
        obj = URL.createObjectURL(r.data);
        setUrl(obj);
      } catch {
        if (!cancelled && attempt < 2) setTimeout(() => load(attempt + 1), 800 * (attempt + 1));
      }
    };
    load();
    return () => { cancelled = true; if (obj) setTimeout(() => URL.revokeObjectURL(obj), 1000); };
  }, [hasLogo, version, path]);
  return url;
};

export const LogoUploader = ({ settings, onChange }) => {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const url = useLogoUrl(!!settings.logo_path, version);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data); setVersion((v) => v + 1); toast.success("Logo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try { const r = await api.delete("/settings/logo"); onChange(r.data); toast.success("Logo removed"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
      <div data-testid="settings-logo-preview" className="h-24 w-48 rounded-lg border border-dashed border-border bg-secondary/50 grid place-items-center overflow-hidden shrink-0">
        {url ? <img src={url} alt="Company logo" className="max-h-full max-w-full object-contain p-2" /> : <span className="text-xs text-muted-foreground">No logo yet</span>}
      </div>
      <div className="space-y-2">
        <input ref={inputRef} data-testid="settings-logo-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} />
        <div className="flex gap-2">
          <Button type="button" data-testid="settings-logo-upload-button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
            <ImagePlus className="h-4 w-4 mr-2" /> {busy ? "Working..." : settings.logo_path ? "Replace logo" : "Upload logo"}
          </Button>
          {settings.logo_path && (
            <Button type="button" data-testid="settings-logo-remove-button" variant="ghost" disabled={busy} onClick={remove} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">PNG, JPG or WEBP up to 2 MB. Shown at the top of your invoices and emails (about 200px tall max).</p>
      </div>
    </div>
  );
};
